import { useMemo, useCallback, useEffect, useState } from 'react';
import { Col, Row, Form } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useNavigate } from 'hooks/useNavigate';
import { useParams } from 'react-router-dom';
import { useApi } from 'hooks/useApi';
import ContactsApi from 'libs/api/leasing/contacts';
import ContactRoleApi from 'libs/api/leasing/contactRole';
import ManagerApi from 'libs/api/leasing/manager';
import moment from 'moment';
import { useFilters } from '../../../admin/subPages/calendars/useFilters';

const api = new ContactsApi();
const contactRoleApi = new ContactRoleApi();
const managerApi = new ManagerApi();
/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{
 *  columns: any,
 *  data: any,
 *  singleItem: any,
 *  loading: boolean,
 *  updateInProcess: boolean,
 *  setUpdateInProcess: func,
 *  onDelete: func,
 *  onChangeTableFilter: func
 * }} state and callbacks for ExperienceYears page
 */

const useLeasingContacts = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const pages = useRecoilValue(getPage);
  const [contactRoles, setContactRoles] = useState([]);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const { backendFilters } = useFilters();

  const {
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    handleDownloadExcel,
  } = useApi({
    resourceKey: 'contacts',
    singleResourceKey: 'contact',
    api,
    isMainPage,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Nome'),
        dataIndex: 'name',
        fixed: 'left',
      },
      {
        title: t('Cognome'),
        dataIndex: 'surname',
      },
      {
        title: t('Ruolo'),
        dataIndex: 'leasings_contact_role',
      },
      {
        title: t('Azienda'),
        dataIndex: 'company_title',
      },
      {
        title: t('Email'),
        dataIndex: 'email',
      },
      {
        title: t('Cellulare'),
        dataIndex: 'cellphone',
      },
      {
        title: t('Rif. leasing'),
        dataIndex: 'leasings_manager',
        render: (data, row) => {
          return <div>{data?.name}</div>;
        },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, pages?.ability?.can_read]);

  const handleCreateOrUpdate = useCallback(
    async (values) => {
      setUpdateInProcess(true);

      const data = {
        contact: {
          ...values,
        },
      };

      try {
        if (!id) {
          await api.create({ data });
          navigate(UIHelper.getListRoute(pathname, 1));
        } else {
          await api.put(id, { data });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        const errors = e.response.data || {};
        if (errors.status !== 500) {
          form.setFields(
            ErrorHelper.getErrorsForForm(errors, {
              leasings_contact_role: 'leasings_contact_role_id',
              leasings_manager: 'leasings_manager_id',
            }),
          );
        }
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, pathname, id],
  );

  useEffect(() => {
    if (id && !isMainPage && singleItem) {
      let fieldsValues = [];
      for (let key in singleItem) {
        switch (key) {
          case 'lasttime_contact_date':
          case 'first_visit_date': {
            fieldsValues.push({
              name: key,
              value: moment(singleItem[key], 'YYYY-MM-DD'),
            });
            break;
          }
          case 'leasings_manager':
          case 'manager_user_id':
            break;
          default: {
            fieldsValues.push({
              name: key,
              value: singleItem[key],
            });
          }
        }
      }
      if (singleItem.leasings_manager) {
        fieldsValues.push({
          name: 'leasings_manager_id',
          value: singleItem.leasings_manager?.id,
        });
      }
      console.log('fieldsValues=', fieldsValues);
      form.setFields(fieldsValues);
    }
  }, [singleItem, form, id, isMainPage]);

  const managers = useMemo(() => {
    return backendFilters?.leasings_managers || [];
  }, [backendFilters]);

  useEffect(() => {
    // (async () => {
    //   try {
    //     const {
    //       data: { contact_roles },
    //     } = await contactRoleApi.list({
    //       params: { per: 1000 },
    //     });
    //     const {
    //       data: { managers },
    //     } = await managerApi.list({
    //       params: { per: 1000 },
    //     });
    //
    //     setContactRoles(contact_roles);
    //     setManagers(managers);
    //   } catch (e) {
    //     console.log(e);
    //   }
    // })();
  }, []);

  const filters = useMemo(
    () => [
      {
        title: t('Azienda'),
        type: 'input',
        name: 'by_company_title',
      },
      {
        title: t('Email'),
        type: 'input',
        name: 'by_email',
      },
      {
        title: t('Nome'),
        type: 'input',
        name: 'by_name',
      },
      {
        title: t('Cognome'),
        type: 'input',
        name: 'by_surname',
      },
      {
        title: t('Ruolo'),
        type: 'select',
        name: 'by_role',
        values: backendFilters?.leasings_contact_roles?.map((i) => ({
          title: i.title,
          value: i.value,
        })),
      },
      // {
      //   title: t('Leasing Manager'),
      //   type: 'select',
      //   name: 'by_manager_user_id',
      //   values: managers.map((i) => ({
      //     title: i.title,
      //     value: i.value,
      //   })),
      // },
      {
        title: t('Attive/disattive'),
        type: 'select',
        name: 'by_status',
        isSearch: true,
        values: [
          {
            title: 'Attive',
            value: 1,
          },
          {
            title: 'Disattive',
            value: 0,
          },
        ],
      },
    ],
    [t, contactRoles, managers, backendFilters],
  );

  return {
    form,
    columns,
    data,
    singleItem,
    loading,
    onDelete,
    updateInProcess,
    handleCreateOrUpdate,
    onChangeTableFilter,
    contactRoles: backendFilters?.leasings_contact_roles || [],
    managers,
    filters,
    handleDownloadExcel,
  };
};

export default useLeasingContacts;
